import {computed, inject, Injectable, signal} from '@angular/core';

import {lastValueFrom} from 'rxjs';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {
  AccountingAccountDetails,
  AccountingAccountService,
  AccountingAccountSummary,
  AccountType,
  CreateAccountingAccountRequest,
  UpdateAccountingAccountRequest
} from '../../../../../../../../api';
import {QueryKey} from '../../../../../../../constants/query-key';

interface AccountingAccountFilter {
  searchTerm?: string | null;
  accountTypes?: AccountType[] | null
}

@Injectable({providedIn: 'root'})
export class AccountingAccountStoreService {
  private readonly accountingAccountService = inject(AccountingAccountService);
  private readonly queryClient = inject(QueryClient);

  selectedAccountingCodeId = signal<number | null>(null);
  filterParams = signal<AccountingAccountFilter>({searchTerm: null, accountTypes: null});

  private readonly accountingAccountsQuery = injectQuery(() => ({
    queryKey: [QueryKey.ACCOUNTING_ACCOUNTS, this.filterParams()],
    queryFn: () => {
      const {searchTerm, accountTypes} = this.filterParams();
      return lastValueFrom(this.accountingAccountService.getAccountingAccounts(
        searchTerm ?? null,
        accountTypes ?? [],
      ));
    },
  }));

  accountingCodes = computed<AccountingAccountSummary[]>(() => this.accountingAccountsQuery.data()?.accountingAccounts ?? []);
  isLoading = computed(() => this.accountingAccountsQuery.isLoading());

  selectedAccountingCodeQuery = injectQuery(() => ({
    queryKey: [QueryKey.ACCOUNTING_ACCOUNT, this.selectedAccountingCodeId()],
    queryFn: () => lastValueFrom(this.accountingAccountService.getAccountingAccountById(this.selectedAccountingCodeId()!)),
    enabled: this.selectedAccountingCodeId() !== null,
  }));
  selectedAccountingAccount = computed<AccountingAccountDetails | null>(() => this.selectedAccountingCodeQuery.data()?.accountingAccount ?? null);
  isLoadingSelectedAccountingCode = computed(() => this.selectedAccountingCodeQuery.isLoading());

  createAccountingAccountMutation = injectMutation(() => ({
    mutationFn: (request: CreateAccountingAccountRequest) =>
      lastValueFrom(this.accountingAccountService.createAccountingAccount(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.ACCOUNTING_ACCOUNTS]});
    },
  }));

  updateAccountingAccountMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateAccountingAccountRequest }) =>
      lastValueFrom(this.accountingAccountService.updateAccountingAccount(request.id, request.body)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.ACCOUNTING_ACCOUNTS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.ACCOUNTING_ACCOUNT, this.selectedAccountingCodeId()]});
    },
  }));

  deleteAccountingAccountMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.accountingAccountService.deleteAccountingAccount(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.ACCOUNTING_ACCOUNTS]});
    },
  }));
}
